import React from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectBusinessInventoryList } from 'redux/selectors';
import { useTranslation } from 'react-i18next';
import CustomSwitch from 'components/Forms/CustomSwitch';
import CustomTextInput from 'components/Forms/CustomTextInput';
import { Box, Grid, Typography, Divider, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useInventoryForm from 'components/MenuNew/hooks/useInventoryForm';
import InventoryActions from 'components/MenuNew/FormView/Inventory/InventoryActions';
import InventoryBarcodes from 'components/MenuNew/FormView/Inventory/InventoryBarcodes';
import Preloader from 'components/Preloader';
import useInventoryHook from 'components/MenuNew/hooks/useInventoryHook';
import noInventoryImage from 'assets/images/no_inventory.png';

// Optimized clickable field wrapper component
const ClickableField = React.memo(
  ({ children, onClick, isDisabled, sx = {} }) => (
    <Box
      sx={{
        width: '100%',
        minHeight: '60px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        ...sx,
      }}
      onClick={isDisabled ? undefined : onClick}
    >
      {children}
    </Box>
  )
);
ClickableField.displayName = 'ClickableField';

// Optimized stock action buttons component
const StockActionButtons = React.memo(
  ({ isDisabled, onRemove, onAdd, onAdjust }) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Button
        disabled={isDisabled}
        color="error"
        size="small"
        variant="outlined"
        onClick={onRemove}
        sx={{ minWidth: 'auto' }}
      >
        Remove
      </Button>
      <Button
        disabled={isDisabled}
        color="primary"
        size="small"
        variant="outlined"
        onClick={onAdd}
        sx={{
          minWidth: 'auto',
          borderColor: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.light',
          },
        }}
      >
        Add
      </Button>
      <Button
        disabled={isDisabled}
        size="small"
        variant="outlined"
        onClick={onAdjust}
        sx={{ minWidth: 'auto' }}
      >
        Adjust
      </Button>
    </Box>
  )
);
StockActionButtons.displayName = 'StockActionButtons';

const ProductFormStockInventory = React.memo(
  ({
    productFormMethods,
    onAddInventory,
    isGenerateInventoryLoading = false,
  }) => {
    const { t } = useTranslation();

    const {
      inventoryFormMethods,
      control,
      handleSubmit,
      openUpdateModal,
      handleClose,
      currentType,
      //handleSelectInventory,
      selectedInventory,
      handleTrackInventory,
      handleUseInventory,
      isDisabled,
      handleSkuClick,
      handleUnitClick,
      handleBarcodeClick,
      uomMenuItems,
    } = useInventoryForm(productFormMethods);

    const { isLoading } = useInventoryHook(productFormMethods);
    const businessInventoryList = useSelector(selectBusinessInventoryList);
    const linkedInventory = useWatch({
      control: productFormMethods.control,
      name: 'inventory',
    });

    if (isLoading) {
      return <Preloader overlay={true} />;
    }

    // Empty state when the item has no main inventory, or its main inventory
    // id points to an inventory product that does not exist.
    const mainInventoryUuid = linkedInventory?.find((item) => item.isMain)?.uuid;
    const hasMainInventory =
      Boolean(mainInventoryUuid) &&
      Boolean(
        businessInventoryList?.some(
          (inventory) => inventory.uuid === mainInventoryUuid
        )
      );
    if (!hasMainInventory) {
      return (
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: 280,
          }}
        >
          <Box
            component="img"
            src={noInventoryImage}
            alt=""
            sx={{ width: 48, height: 48, mb: 2 }}
          />
          <Box
            sx={{
              width: 470,
              minHeight: 88,
              gap: '8px',
              opacity: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: 24,
                lineHeight: '32px',
                letterSpacing: 0.25,
                textAlign: 'center',
                verticalAlign: 'middle',
              }}
            >
              {t('No Inventory Information Yet')}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: 16,
                lineHeight: '24px',
                letterSpacing: 0.25,
                textAlign: 'center',
                verticalAlign: 'middle',
                color: 'text.secondary',
              }}
            >
              {t(
                'It looks like there is no inventory data available. Please add new inventory items'
              )}
            </Typography>
          </Box>
          <LoadingButton
            variant="contained"
            onClick={onAddInventory}
            loading={isGenerateInventoryLoading}
            disabled={isGenerateInventoryLoading}
            sx={{
              minWidth: 157,
              height: 36,
              borderRadius: 1,
              py: 2,
              px: 4,
              backgroundColor: '#2D7FF9',
              '&:hover': {
                backgroundColor: '#2566d4',
              },
            }}
          >
            {t('Add Inventory')}
          </LoadingButton>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3 }}>
        <InventoryActions
          open={openUpdateModal}
          onClose={handleClose}
          inventoryFormMethods={inventoryFormMethods}
          currentType={currentType}
          selectedInventory={selectedInventory}
          uuid={inventoryFormMethods?.getValues()?.uuid}
          productId={productFormMethods.getValues()?._id}
        />
        <FormProvider {...inventoryFormMethods}>
          <form onSubmit={inventoryFormMethods.handleSubmit(handleSubmit)}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <CustomSwitch
                  name="useInventory"
                  label={t('No Inventory Item')}
                  onChange={handleUseInventory}
                />
                <Divider sx={{ mt: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Inventory Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  {/* SKU Field */}
                  <Grid item xs={12} md={6}>
                    <ClickableField
                      onClick={handleSkuClick}
                      isDisabled={isDisabled}
                    >
                      <CustomTextInput
                        disabled={isDisabled}
                        name="sku"
                        label="SKU"
                        size="small"
                        sx={{ width: '100%', minHeight: '60px' }}
                        placeholder="Enter SKU code"
                        InputProps={{ readOnly: true }}
                      />
                    </ClickableField>
                  </Grid>

                  {/* Unit of Measure Field */}
                  <Grid item xs={12} md={6}>
                    <ClickableField
                      onClick={handleUnitClick}
                      isDisabled={isDisabled}
                    >
                      <CustomTextInput
                        disabled={isDisabled}
                        name="unit"
                        label="Unit of Measure"
                        select
                        size="small"
                        sx={{ width: '100%', minHeight: '60px' }}
                        InputProps={{ readOnly: true }}
                      >
                        {uomMenuItems}
                      </CustomTextInput>
                    </ClickableField>
                  </Grid>

                  {/* Stock Quantity */}
                  <Grid item xs={12}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'self-start', gap: 1 }}
                    >
                      <CustomTextInput
                        name="stock"
                        label="Stock Quantity"
                        size="small"
                        sx={{ flex: 1, minHeight: '60px' }}
                        inputProps={{
                          type: 'number',
                          min: 0,
                          step: 1,
                        }}
                        disabled={true}
                      />
                      {/* <StockActionButtons
                        isDisabled={isDisabled}
                        onRemove={handleStockRemove}
                        onAdd={handleStockAdd}
                        onAdjust={handleStockAdjust}
                      /> */}
                    </Box>
                  </Grid>

                  {/* Barcodes Section */}
                  <Grid item xs={12}>
                    <ClickableField
                      onClick={handleBarcodeClick}
                      isDisabled={isDisabled}
                    >
                      <InventoryBarcodes
                        name="barcodeNumber"
                        label="Barcodes"
                        control={control}
                        readOnly={true}
                        isDisabled={isDisabled}
                      />
                    </ClickableField>
                  </Grid>

                  {/* Track Inventory Switch */}
                  <Grid item xs={12}>
                    <Box sx={{ width: '100%', cursor: 'pointer' }}>
                      <CustomSwitch
                        disabled={isDisabled}
                        name="trackInventory"
                        label={t('Track Inventory')}
                        onChange={handleTrackInventory}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    );
  }
);
ProductFormStockInventory.displayName = 'ProductFormStockInventory';

export default ProductFormStockInventory;
